import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { BASE_URL } from "../../components/config/config";
import {
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Avatar,
  Container,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import moment from "moment";
import DeleteAlertDialogModal from "../../components/Components/DeleteAlert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ViewVerify from "../../components/AdminApi/ViewVerify";
import StarIcon from "@mui/icons-material/Star";
import { useNavigate, useParams } from "react-router-dom";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";

const ViewCenter = (props) => {
  const navigate = useNavigate();
  const { centerId } = useParams();
  const reduxState = useSelector((state) => state.userReducer);
  const [centerDetails, setCenterDetails] = useState(null);
  const [technicianDetails, setTechnicianDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteAlertPopupOpen, setIsDeleteAlertPopupOpen] = useState(false);
  const [isVerificationPopupOpen, setIsVerificationPopupOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    duration: 5000,
    type: "",
  });

  const adminId = reduxState?.adminDetails?._id;
  const isSuperAdmin = reduxState?.adminDetails?.admin_role === "SUPER_ADMIN";

  useEffect(() => {
    fetchCenterDetails(centerId);
  }, [centerId]);

  const fetchCenterDetails = async (centerId) => {
    setIsLoading(true);
    const accessToken = localStorage.getItem("access_token");

    try {
      const response = await axios.get(
        `${BASE_URL}admin/getCenterDetails/${centerId}`,
        {
          headers: {
            "x-access-token": accessToken,
          },
        }
      );
      setCenterDetails(response.data?.data?.CenterDetails?.cenData);
      setTechnicianDetails(response.data?.data?.TechnicianDetails?.techData);
    } catch (error) {
      console.error("Error fetching center details:", error);
      setError("Failed to load center details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCenter = async () => {
    setIsLoading(true);
    const accessToken = localStorage.getItem("access_token");

    try {
      await axios.delete(`${BASE_URL}admin/deleteCenter/${centerDetails._id}`, {
        headers: {
          "x-access-token": accessToken,
        },
      });
      setToast({
        open: true,
        msg: "Center deleted successfully",
        duration: 5000,
        type: "success",
      });
      setIsDeleteAlertPopupOpen(false);
      navigate("/centers");
    } catch (error) {
      // console.error("Error deleting center:", error);
      setToast({
        open: true,
        msg: "Failed to delete center",
        duration: 5000,
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDeleteModal = () => {
    setIsDeleteAlertPopupOpen(!isDeleteAlertPopupOpen);
  };

  const toggleVerificationDetailsPopup = () => {
    setIsVerificationPopupOpen(!isVerificationPopupOpen);
  };

  const closeDialog = () => {
    setToast({
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    });
  };

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const allPrimaryServices = [
    {
      _id: "6373436f1307e26d44ac8cdb",
      service_name: "AIR CONDITIONER - AND GEYSER",
    },
    {
      _id: "637b76f47c7cd9e139b39d02",
      service_name:
        "ELECTRICIAN - FAN, TUBE, INVERTER, HOUSE WIRING, MIXER, TOASTER, GRINDER ETC",
    },
    {
      _id: "64462802f77b1ff1d68890fd",
      service_name: "DRIVER (CAR, BIKE) - PICKUP AND DROP SERVICE",
    },
    {
      _id: "6455f89054106f153e47142f",
      service_name:
        "CARPENTER - ASSEMBLY/FITTINGS, DOOR/WINDOW, FURNITURE REPAIR ETC",
    },
    {
      _id: "637b79cd7c7cd9e139b39d17",
      service_name:
        "CLEANING SERVICES - CLEANING, SWEEPING, DUSTING, UTENSILS ETC..",
    },
    {
      _id: "637b7a0e7c7cd9e139b39d1e",
      service_name:
        "PLUMBING - SHOWER, TAP, MIXER, CISTERN AND SEAT, OVERHEAD TANK FITTING ETC",
    },
    {
      _id: "676508ba465c8c3892d25861",
      service_name: "LIFT REPAIR AND MAINTENANCE",
    },
    {
      _id: "65dd7f2c181db7734209e2a7",
      service_name: "DIGITAL DEVICES - CCTV, LAPTOP, PRINTER, MOBILES, DTH ETC",
    },
    {
      _id: "6455f90254106f153e471437",
      service_name:
        "HOME APPLIANCE - WASHING MACHINE, REFRIGERATOR, MWO, TV ETC",
    },
    {
      _id: "637b79997c7cd9e139b39d10",
      service_name:
        "BEAUTY AND PERSONAL CARE - SPA, MASSAGE, FACIAL, HAIR SERVICES, MAKEUP, MEHNDI ART",
    },
    {
      _id: "637b788c7c7cd9e139b39d09",
      service_name: "KITCHEN APPLIANCE - WATER PURIFIER, CHIMNEY ETC",
    },
  ];

  const allSecondaryServices = [
    {
      _id: "6373436f1307e26d44ac8cdb",
      service_name: "AIR CONDITIONER - AND GEYSER",
    },
    {
      _id: "637b76f47c7cd9e139b39d02",
      service_name:
        "ELECTRICIAN - FAN, TUBE, INVERTER, HOUSE WIRING, MIXER, TOASTER, GRINDER ETC",
    },
    {
      _id: "64462802f77b1ff1d68890fd",
      service_name: "DRIVER (CAR, BIKE) - PICKUP AND DROP SERVICE",
    },
    {
      _id: "6455f89054106f153e47142f",
      service_name:
        "CARPENTER - ASSEMBLY/FITTINGS, DOOR/WINDOW, FURNITURE REPAIR ETC",
    },
    {
      _id: "637b79cd7c7cd9e139b39d17",
      service_name:
        "CLEANING SERVICES - CLEANING, SWEEPING, DUSTING, UTENSILS ETC..",
    },
    {
      _id: "637b7a0e7c7cd9e139b39d1e",
      service_name:
        "PLUMBING - SHOWER, TAP, MIXER, CISTERN AND SEAT, OVERHEAD TANK FITTING ETC",
    },
    {
      _id: "676508ba465c8c3892d25861",
      service_name: "LIFT REPAIR AND MAINTENANCE",
    },
    {
      _id: "65dd7f2c181db7734209e2a7",
      service_name: "DIGITAL DEVICES - CCTV, LAPTOP, PRINTER, MOBILES, DTH ETC",
    },
    {
      _id: "6455f90254106f153e471437",
      service_name:
        "HOME APPLIANCE - WASHING MACHINE, REFRIGERATOR, MWO, TV ETC",
    },
    {
      _id: "637b79997c7cd9e139b39d10",
      service_name:
        "BEAUTY AND PERSONAL CARE - SPA, MASSAGE, FACIAL, HAIR SERVICES, MAKEUP, MEHNDI ART",
    },
    {
      _id: "637b788c7c7cd9e139b39d09",
      service_name: "KITCHEN APPLIANCE - WATER PURIFIER, CHIMNEY ETC",
    },
  ];

  const allClientsList = [
    {
      _id: "6864e56afe8a92be47300d6e",
      client_name: "Dev",
    },
    {
      _id: "67fccd44cbebfea89eed57df",
      client_name: "FKJEEVES",
    },
    {
      _id: "68a94a63e75525e47dadf546",
      client_name: "Life Long",
    },
    {
      _id: "68653fba6a9c25e47b645c34",
      client_name: "Unique Fans",
    },
  ];

  const getServiceNameById = (id, serviceList) => {
    const service = serviceList.find((item) => item._id === id);
    return service?.service_name?.split("-")[0]?.trim() || null;
  };

  const getFormattedPrimaryServices = (ids, allServices) => {
    if (!ids || ids.length === 0) return "N/A";
    return ids
      .map((id) => getServiceNameById(id, allServices))
      .filter(Boolean)
      .join(", ");
  };

  const getFormattedSecondaryServices = (secondaryItems, allServices) => {
    if (!secondaryItems || secondaryItems.length === 0) return "N/A";
    return secondaryItems
      .map((item) =>
        getServiceNameById(item.secondary_services_id, allServices)
      )
      .filter(Boolean)
      .join(", ");
  };

  const getClientNames = (idsList, allClients) => {
    if (!Array.isArray(idsList)) return "N/A";

    const names = idsList
      .map((id) => {
        const match = allClients.find((client) => client._id === id);
        return match ? match.client_name : null;
      })
      .filter((name) => name !== null);

    return names.length > 0 ? names.join(", ") : "N/A";
  };

  const renderStatusChip = (status) => {
    return status ? (
      <Chip
        icon={<VerifiedIcon style={{ color: "white" }} />}
        label="VERIFIED"
        color="success"
        size="small"
        sx={{ fontWeight: 600 }}
      />
    ) : (
      <Chip
        icon={<CancelIcon style={{ color: "white" }} />}
        label="NOT VERIFIED"
        color="error"
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const renderTableSection = (title, data) => {
    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table size="small">
            <TableBody>
              {Object.entries(data).map(([key, value]) => (
                <TableRow key={key} hover>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      width: "40%",
                      backgroundColor: "grey.50",
                      fontSize: "0.95rem",
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    {key.replace(/_/g, " ").toUpperCase()}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "60%",
                      fontSize: "0.95rem",
                    }}
                  >
                    {Array.isArray(value)
                      ? value.join(", ")
                      : typeof value === "boolean"
                      ? renderStatusChip(value)
                      : value === null || value === undefined
                      ? "N/A"
                      : value.toString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  if (isLoading) {
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  if (!centerDetails) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography>No center details found</Typography>
      </Paper>
    );
  }

  // Prepare data for each section
  const personalDetails = {
    // "Center Name": centerDetails.center_name || "N/A",
    "Contact Person": centerDetails?.personal_details?.name || "N/A",
    Email: centerDetails?.personal_details?.email || "N/A",
    "Mobile Number": isSuperAdmin
      ? centerDetails?.personal_details?.phone?.mobile_number
      : "*****",
    "Alternate Number": isSuperAdmin
      ? centerDetails?.personal_details?.phone?.alternate_number
      : "*****",
    "Date of Birth": centerDetails?.personal_details?.dob
      ? moment(centerDetails.personal_details.dob).format("DD-MM-YYYY")
      : "N/A",
    "Work Experience":
      centerDetails?.personal_details?.work_experiance || "N/A",
  };

  const addressDetails = {
    Address: centerDetails?.address_details?.address_line || "N/A",
    City: centerDetails?.address_details?.city || "N/A",
    State: centerDetails?.address_details?.state || "N/A",
    Pincode: centerDetails?.address_details?.pincode || "N/A",
    "Additional Pincodes": Array.isArray(
      centerDetails?.address_details?.additional_pincode
    )
      ? centerDetails.address_details.additional_pincode.join(", ")
      : "N/A",
  };

  const serviceDetails = {
    "Primary Services": getFormattedPrimaryServices(
      centerDetails?.services?.primary_services,
      allPrimaryServices
    ),
    "Secondary Services": getFormattedSecondaryServices(
      centerDetails?.services?.secondary_services,
      allSecondaryServices || "N/A"
    ),
    "Service Pin Codes": centerDetails?.services?.pincodes?.join(", ") || "N/A",
    "Ticket Accepting Limit": centerDetails?.ticket_accept_limit || "N/A",
    "Authorized for Clients": getClientNames(
      centerDetails?.clients_ids_list,
      allClientsList
    ),
  };

  const paymentDetails = {
    "Onboarding Kit Paid": centerDetails?.payment_details
      ?.paid_for_onboarding_kit
      ? "Yes"
      : "No",
    "Subscription Start Date":
      centerDetails?.payment_details?.subscribe_start_date || "N/A",
    "Subscription End Date":
      centerDetails?.payment_details?.subscribe_end_date || "N/A",
  };

  const qrDetails = {
    "QR ID": centerDetails?.qr_details?.qr_id || "N/A",
    "QR Status": centerDetails?.qr_details?.qrStatus ? "Active" : "Inactive",
    "Referral Code": centerDetails?.referral_received || "N/A",
  };

  const verificationDetails = {
    "Created At": moment(centerDetails.createdAt).format("DD-MM-YYYY"),
    "Verification Status": centerDetails?.verification_status || "N/A",
    "PAN Verified": centerDetails?.document_details?.pan_verified || false,
    "Aadhar Front Verified":
      centerDetails?.document_details?.aadhar_card_document
        ?.aadhar_front_verified || false,
    "Aadhar Back Verified":
      centerDetails?.document_details?.aadhar_card_document
        ?.aadhar_back_verified || false,
  };

  const statsDetails = {
    "Closed Tickets": centerDetails?.count_details?.closed_ticket_count || "0",
    "Average Rating":
      centerDetails?.count_details?.average_ticket_rating || "0",
    "Account Status": centerDetails.is_active === 1 ? "Active" : "Inactive",
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={toast.open}
        autoHideDuration={toast.duration}
        onClose={closeDialog}
      >
        <Alert
          onClose={closeDialog}
          severity={toast.type}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isSmallScreen ? "flex-start" : "center",
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {centerDetails.center_name}
            </Typography>

            {centerDetails.personal_details?.profile_picture && (
              <img
                src={centerDetails.personal_details.profile_picture}
                alt="Profile"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              alignItems: isSmallScreen ? "stretch" : "center",
              gap: 1,
              width: isSmallScreen ? "100%" : "auto",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/admin-centers")}
              fullWidth={isSmallScreen}
            >
              Back
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/add-center?edit=${centerDetails._id}`)}
              fullWidth={isSmallScreen}
            >
              Edit
            </Button>

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={toggleDeleteModal}
              fullWidth={isSmallScreen}
            >
              Delete
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Center Overview */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, height: "100%", borderRadius: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Avatar
                  sx={{
                    bgcolor:
                      statsDetails["Account Status"] === "Active"
                        ? "success.light"
                        : "error.light",
                    mr: 1,
                    width: 24,
                    height: 24,
                  }}
                >
                  {statsDetails["Account Status"] === "Active" ? (
                    <VerifiedIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <CancelIcon sx={{ fontSize: 16 }} />
                  )}
                </Avatar>
                <Typography variant="h6">
                  {statsDetails["Account Status"]}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, height: "100%", borderRadius: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Subscription
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Avatar
                  sx={{
                    bgcolor:
                      paymentDetails["Onboarding Kit Paid"] === "Yes"
                        ? "success.light"
                        : "error.light",
                    mr: 1,
                    width: 24,
                    height: 24,
                  }}
                >
                  {paymentDetails["Onboarding Kit Paid"] === "Yes" ? (
                    <VerifiedIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <CancelIcon sx={{ fontSize: 16 }} />
                  )}
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      paymentDetails["Onboarding Kit Paid"] === "Yes"
                        ? "success.main"
                        : "error.main",
                  }}
                >
                  {paymentDetails["Onboarding Kit Paid"] === "Yes"
                    ? "Subscribed"
                    : "Not Subscribed"}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, height: "100%", borderRadius: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Closed Requests
              </Typography>
              <Typography variant="h4" sx={{ mt: 1 }}>
                {statsDetails["Closed Tickets"]}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, height: "100%", borderRadius: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Average Rating
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <StarIcon sx={{ color: "gold", mr: 1 }} />
                <Typography variant="h4">
                  {statsDetails["Average Rating"]}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content Sections */}
      <Grid container spacing={3}>
        {/* First Row - Personal and Address Details */}
        <Grid item xs={12} md={6}>
          {renderTableSection("Personal Information", personalDetails)}
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Verification Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow hover>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: "40%",
                        backgroundColor: "grey.50",
                        fontSize: "0.95rem",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      OTP VERIFICATION
                    </TableCell>
                    <TableCell>
                      {renderStatusChip(
                        centerDetails?.otp_details?.is_otp_verfied
                      )}
                    </TableCell>
                  </TableRow>
                  {Object.entries(verificationDetails).map(([key, value]) => (
                    <TableRow key={key} hover>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "40%",
                          backgroundColor: "grey.50",
                          fontSize: "0.95rem",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {key.replace(/_/g, " ").toUpperCase()}
                      </TableCell>
                      <TableCell>
                        {typeof value === "boolean"
                          ? renderStatusChip(value)
                          : value}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow hover>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: "40%",
                        backgroundColor: "grey.50",
                        fontSize: "0.95rem",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      DOCUMENTS
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={toggleVerificationDetailsPopup}
                        size="small"
                      >
                        View Documents
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Second Row - Service and Payment Details */}
        <Grid item xs={12} md={6}>
          {renderTableSection("Service Information", serviceDetails)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderTableSection("Payment Information", paymentDetails)}
        </Grid>

        {/* Third Row - QR and Verification Details */}
        <Grid item xs={12} md={6}>
          {renderTableSection("Address Information", addressDetails)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderTableSection("QR Code Information", qrDetails)}
        </Grid>

        {/* Fourth Row - Stats Details (full width) */}
        <Grid item xs={12}>
          {renderTableSection("Statistics", statsDetails)}
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <DeleteAlertDialogModal
        openAlert={isDeleteAlertPopupOpen}
        handleAlertClose={toggleDeleteModal}
        handleDeleteTicket={handleDeleteCenter}
        Heading="Center"
      />

      {/* Verification Documents Dialog */}
      <ViewVerify
        isVerificationPopupOpen={isVerificationPopupOpen}
        toggleVerificationDetailsPopup={toggleVerificationDetailsPopup}
        selectedCenterData={centerDetails}
        technicianDetails={technicianDetails}
        adminId={adminId}
        selectedCenterId={centerDetails._id}
        panVerified={centerDetails?.document_details?.pan_verified}
        aadhar_font={
          centerDetails?.document_details?.aadhar_card_document
            ?.aadhar_front_verified
        }
        aadhar_back={
          centerDetails?.document_details?.aadhar_card_document
            ?.aadhar_back_verified
        }
        onDocumentsUpdate={fetchCenterDetails}
      />
    </Container>
  );
};

export default ViewCenter;
